import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Plus, X, Settings, Calendar, Gauge, Bus as BusIcon, CreditCard, Hash, Car, Sparkles, Image, Wrench, Check, User, FileText } from 'lucide-react';
import ParameterTypesDialog from './ParameterTypesDialog';
import ImageCropDialog from './ImageCropDialog';
import { formatDate, toInputDateFormat } from '@/utils/dateFormat';
import { DateInput } from '@/components/ui/date-input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
});

export default function BusFormDialog({ open, onOpenChange, bus, onSave }) {
  const [formData, setFormData] = useState({
    vin: '',
    brand: '',
    model: '',
    license_plate: '',
    mileage: '',
    mileage_note: '',
    notes: '',
    is_personal: false,
    seats: '',
    red_threshold_low: 20,
    yellow_threshold_low: 25,
    yellow_threshold_high: 30,
    red_threshold_high: 35,
  });
  const [parameters, setParameters] = useState([]);
  const [activeTab, setActiveTab] = useState('service'); // 'service' or 'info'
  
  // Логируем изменения parameters
  useEffect(() => {
    console.log('Parameters changed:', parameters);
  }, [parameters]);
  const [parameterTypes, setParameterTypes] = useState([]);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [tempImageSrc, setTempImageSrc] = useState(null);
  const [cropDialogOpen, setCropDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [typesDialogOpen, setTypesDialogOpen] = useState(false);

  useEffect(() => {
    if (open) {
      fetchParameterTypes();
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      fetchParameterTypes();
    }
  }, [open]);

  useEffect(() => {
    if (open && bus) {
      setFormData({
        vin: bus.vin,
        brand: bus.brand,
        model: bus.model,
        license_plate: bus.license_plate,
        mileage: bus.mileage.toString(),
        mileage_note: bus.mileage_note || '',
        notes: bus.notes || '',
        is_personal: bus.is_personal || false,
        seats: bus.seats ? bus.seats.toString() : '',
        red_threshold_low: bus.red_threshold_low || 20,
        yellow_threshold_low: bus.yellow_threshold_low || 25,
        yellow_threshold_high: bus.yellow_threshold_high || 30,
        red_threshold_high: bus.red_threshold_high || 35,
      });
      setParameters(bus.parameters || []);
      setPhotoPreview(bus.photo);
      setActiveTab('service'); // Always start with service tab
    } else if (open && !bus) {
      setFormData({
        vin: '',
        brand: '',
        model: '',
        license_plate: '',
        mileage: '',
        notes: '',
        is_personal: false,
        seats: '',
        red_threshold_low: 20,
        yellow_threshold_low: 25,
        yellow_threshold_high: 30,
        red_threshold_high: 35,
      });
      setParameters([]);
      setPhotoPreview(null);
      setPhotoFile(null);
      setActiveTab('service');
    }
  }, [open, bus?.id]); // Используем bus?.id вместо bus

  // Убран автоматический пересчет при изменении пробега, так как пользователь может редактировать поля вручную
  // Пересчет происходит только при нажатии кнопок быстрого выбора

  const fetchParameterTypes = async () => {
    try {
      const response = await axios.get(`${API}/parameter-types`, getAuthHeaders());
      setParameterTypes(response.data);
      console.log('Loaded parameter types:', response.data);
    } catch (error) {
      console.error('Error loading parameter types:', error);
      toast.error('Ошибка загрузки типов параметров');
    }
  };

  const addParameter = () => {
    const newParameters = [
      ...parameters,
      {
        parameter_type_id: '',
        parameter_name: '',
        tracking_type: '', // Будет установлен при выборе типа параметра
        start_date: '',
        end_date: '',
        start_mileage: null,
        end_mileage: null,
        selected_km_interval: null,
        description: '',
      },
    ];
    setParameters(newParameters);
    
    // Автоматический скролл к новому параметру
    setTimeout(() => {
      const formElement = document.querySelector('[data-testid="bus-form-dialog"] form');
      if (formElement) {
        // Скроллим к концу формы где находится новый параметр
        formElement.scrollTo({
          top: formElement.scrollHeight,
          behavior: 'smooth'
        });
      }
    }, 100);
  };

  const removeParameter = (index) => {
    setParameters(parameters.filter((_, i) => i !== index));
  };

  const updateParameter = (index, field, value) => {
    console.log(`Updating parameter ${index}, field: ${field}, value:`, value);
    const updated = [...parameters];
    updated[index] = { ...updated[index], [field]: value };

    if (field === 'parameter_type_id') {
      const selectedType = parameterTypes.find(t => t.id === value);
      if (selectedType) {
        updated[index].parameter_name = selectedType.name;
        updated[index].tracking_type = selectedType.tracking_type || 'date';
      }
    }

    // Если пользователь меняет tracking_type вручную, очищаем неиспользуемые поля
    if (field === 'tracking_type') {
      if (value === 'date') {
        // Переключение на дату - очищаем поля пробега
        updated[index].start_mileage = null;
        updated[index].end_mileage = null;
        updated[index].selected_km_interval = null;
      } else if (value === 'mileage') {
        // Переключение на пробег - очищаем поля дат
        updated[index].start_date = '';
        updated[index].end_date = '';
      }
    }

    // Если вручную изменяется start_mileage и есть сохранённый интервал, пересчитать end_mileage
    if (field === 'start_mileage' && updated[index].selected_km_interval) {
      const newStartMileage = parseInt(value) || 0;
      updated[index].end_mileage = newStartMileage + updated[index].selected_km_interval;
    }

    console.log('Updated parameters:', updated);
    setParameters(updated);
  };

  // Функция для быстрого выбора периода (дата)
  const setQuickDatePeriod = (index, months) => {
    const updated = [...parameters];
    
    // Если start_date уже заполнен, используем его, иначе берем текущую дату
    let startDateStr;
    if (updated[index].start_date) {
      startDateStr = updated[index].start_date;
    } else {
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      startDateStr = `${year}-${month}-${day}`;
      updated[index].start_date = startDateStr;
    }
    
    // Рассчитываем end_date от start_date (парсим без timezone)
    const [year, month, day] = startDateStr.split('-').map(Number);
    const endYear = month + months > 12 ? year + Math.floor((month + months - 1) / 12) : year;
    const endMonth = ((month + months - 1) % 12) + 1;
    const endDay = day;
    
    updated[index].end_date = `${endYear}-${String(endMonth).padStart(2, '0')}-${String(endDay).padStart(2, '0')}`;
    setParameters(updated);
  };

  // Функция для быстрого выбора пробега
  const setQuickMileagePeriod = (index, kmAmount) => {
    const updated = [...parameters];
    const currentMileage = parseInt(formData.mileage) || 0;
    
    // Если start_mileage не задан, устанавливаем его равным текущему пробегу
    if (!updated[index].start_mileage || updated[index].start_mileage === 0) {
      updated[index].start_mileage = currentMileage;
    }
    
    const startMileage = parseInt(updated[index].start_mileage) || currentMileage;
    
    // Сохраняем выбранный интервал
    updated[index].selected_km_interval = kmAmount;
    
    // end_mileage = абсолютный пробег когда нужно обслуживание
    updated[index].end_mileage = startMileage + kmAmount;
    
    setParameters(updated);
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setTempImageSrc(reader.result);
        setCropDialogOpen(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCropComplete = (croppedBlob) => {
    // Convert blob to file
    const file = new File([croppedBlob], 'cropped-image.jpg', { type: 'image/jpeg' });
    setPhotoFile(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPhotoPreview(reader.result);
    };
    reader.readAsDataURL(croppedBlob);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const validParameters = parameters.filter(p => {
        if (!p.parameter_type_id) return false;
        if (!p.tracking_type) {
          console.error('Parameter missing tracking_type:', p);
          return false;
        }

        if (p.tracking_type === 'mileage') {
          // Разрешаем отрицательные значения end_mileage (превышение пробега)
          return p.end_mileage !== null && p.end_mileage !== undefined;
        } else {
          return p.start_date && p.end_date;
        }
      });

      if (validParameters.length === 0 && parameters.length > 0) {
        toast.error('Заполните все обязательные поля параметров');
        setLoading(false);
        return;
      }
      
      // Дополнительная проверка что все параметры имеют tracking_type
      const invalidParams = parameters.filter(p => p.parameter_type_id && !p.tracking_type);
      if (invalidParams.length > 0) {
        console.error('Parameters without tracking_type:', invalidParams);
        toast.error('Ошибка: не установлен тип отслеживания. Пожалуйста, выберите тип параметра заново.');
        setLoading(false);
        return;
      }

      const payload = {
        ...formData,
        mileage: parseInt(formData.mileage, 10),
        seats: formData.seats ? parseInt(formData.seats, 10) : null,
        parameters: validParameters.map(p => ({
          parameter_type_id: p.parameter_type_id,
          parameter_name: p.parameter_name,
          tracking_type: p.tracking_type || 'date',
          start_date: p.tracking_type === 'date' ? p.start_date : '',
          end_date: p.tracking_type === 'date' ? p.end_date : '',
          start_mileage: p.tracking_type === 'mileage' ? parseInt(p.start_mileage || 0, 10) : null,
          end_mileage: p.tracking_type === 'mileage' ? parseInt(p.end_mileage || 0, 10) : null,
          selected_km_interval: p.tracking_type === 'mileage' ? (p.selected_km_interval || null) : null,
          description: p.description || ''
        })),
      };

      let busId;
      if (bus) {
        const updatePayload = {
          ...payload,
          parameters: validParameters
        };
        await axios.put(`${API}/buses/${bus.id}`, updatePayload, getAuthHeaders());
        busId = bus.id;
        toast.success('Автобус обновлен');
      } else {
        const response = await axios.post(`${API}/buses`, payload, getAuthHeaders());
        busId = response.data.id;
        toast.success('Автобус добавлен');
      }

      if (photoFile) {
        const formDataPhoto = new FormData();
        formDataPhoto.append('file', photoFile);
        await axios.post(
          `${API}/buses/${busId}/upload-photo`,
          formDataPhoto,
          getAuthHeaders()
        );
      }

      onSave();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка сохранения');
    } finally {
      setLoading(false);
    }
  };

  // Функция для получения кнопок быстрого выбора пробега в зависимости от типа параметра
  const getMileageQuickButtons = (paramName) => {
    const buttons = [];
    
    if (paramName === 'Замена масла') {
      buttons.push(
        { label: '10,000 км', value: 10000 },
        { label: '12,000 км', value: 12000 },
        { label: '15,000 км', value: 15000 },
        { label: '25,000 км', value: 25000 }
      );
    } else {
      // Для всех остальных параметров по пробегу (включая "Масло в коробке" и "Масло в мосту")
      buttons.push(
        { label: '50,000 км', value: 50000 },
        { label: '80,000 км', value: 80000 },
        { label: '100,000 км', value: 100000 }
      );
    }
    
    return buttons;
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-5xl max-h-[95vh] h-full sm:h-auto overflow-x-hidden overflow-y-hidden flex flex-col p-0 bg-white" data-testid="bus-form-dialog">
          <DialogHeader className="px-4 sm:px-6 pt-4 sm:pt-6 pb-4 bg-gradient-to-r from-slate-800 via-slate-900 to-gray-900 text-white shadow-2xl sticky top-0 z-10">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <DialogTitle className="text-xl sm:text-2xl font-bold flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center shadow-lg">
                    <BusIcon className="w-6 h-6" strokeWidth={2.5} />
                  </div>
                  {bus ? '✏️ Редактировать автобус' : '➕ Добавить автобус'}
                </DialogTitle>
                <DialogDescription className="text-gray-300 font-medium text-sm mt-2">
                  Заполните информацию об автобусе и сроки технического обслуживания
                </DialogDescription>
              </div>
              {/* Кнопки в хедере */}
              <div className="flex gap-1.5 sm:gap-2 ml-2 sm:ml-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                  className="border-2 border-white bg-white hover:bg-gray-100 font-semibold text-gray-900 px-2 sm:px-4 text-xs sm:text-sm h-8 sm:h-10"
                >
                  <X className="w-4 h-4 sm:hidden" />
                  <span className="hidden sm:inline">Отмена</span>
                </Button>
                <Button
                  type="submit"
                  form="bus-form"
                  className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white shadow-lg hover:shadow-xl transform hover:scale-105 transition-all font-bold px-2 sm:px-4 text-xs sm:text-sm h-8 sm:h-10"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="sm:hidden">⏳</span>
                      <span className="hidden sm:inline">⏳ Сохранение...</span>
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4 sm:hidden" />
                      <span className="hidden sm:inline">{bus ? '✅ Обновить' : '✨ Сохранить'}</span>
                    </>
                  )}
                </Button>
              </div>
            </div>
          </DialogHeader>

          <form id="bus-form" onSubmit={handleSubmit} className="flex-1 overflow-y-auto overflow-x-hidden flex flex-col bg-gradient-to-b from-gray-50 to-white">
            {/* Tabs */}
            <div className="sticky top-0 z-10 bg-white border-b-2 border-gray-200 px-4 sm:px-6">
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setActiveTab('service')}
                  className={`flex-1 py-3 px-4 font-semibold transition-all ${
                    activeTab === 'service'
                      ? 'text-green-600 border-b-4 border-green-600 bg-green-50'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <Wrench className="w-5 h-5 inline mr-2" />
                  Параметры обслуживания
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('info')}
                  className={`flex-1 py-3 px-4 font-semibold transition-all ${
                    activeTab === 'info'
                      ? 'text-blue-600 border-b-4 border-blue-600 bg-blue-50'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <BusIcon className="w-5 h-5 inline mr-2" />
                  Основная информация
                </button>
              </div>
            </div>

            <div className="flex-1 px-4 sm:px-6 py-4 space-y-6">
              
              {/* Tab: Основная информация */}
              {activeTab === 'info' && (
                <div className="bg-white rounded-xl shadow-sm border-2 border-blue-100 p-4 sm:p-6">
                  <div className="flex items-center gap-2 mb-4 pb-3 border-b-2 border-blue-100">
                    <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                      <BusIcon className="w-5 h-5 text-white" strokeWidth={2.5} />
                    </div>
                    <h3 className="text-lg font-bold text-gray-800">Основная информация</h3>
                  </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Личное авто чекбокс */}
                  <div className="md:col-span-2 space-y-2 mb-2">
                    <div className="flex items-center space-x-2 p-3 bg-yellow-50 border-2 border-yellow-200 rounded-lg">
                      <Checkbox
                        id="is_personal"
                        checked={formData.is_personal}
                        onCheckedChange={(checked) => setFormData({ ...formData, is_personal: checked })}
                        className="w-5 h-5"
                      />
                      <Label
                        htmlFor="is_personal"
                        className="text-sm font-semibold text-gray-800 flex items-center gap-2 cursor-pointer"
                      >
                        <User className="w-4 h-4 text-yellow-600" />
                        Личное авто (не будет отображаться в выборе водителя)
                      </Label>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="vin" className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                      <Hash className="w-4 h-4 text-blue-500" />
                      VIN номер *
                    </Label>
                    <Input
                      id="vin"
                      value={formData.vin}
                      onChange={(e) => setFormData({ ...formData, vin: e.target.value })}
                      required
                      data-testid="vin-input"
                      placeholder="WVWZZZ1KZBW000000"
                      className="h-11 text-sm border-2 border-gray-200 focus:border-blue-500 transition-colors"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="license_plate" className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                      <CreditCard className="w-4 h-4 text-green-500" />
                      Гос. номер *
                    </Label>
                    <Input
                      id="license_plate"
                      value={formData.license_plate}
                      onChange={(e) => setFormData({ ...formData, license_plate: e.target.value })}
                      required
                      data-testid="license-plate-input"
                      placeholder="A123BC 123"
                      className="h-11 text-sm border-2 border-gray-200 focus:border-green-500 transition-colors"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="brand" className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-purple-500" />
                      Марка *
                    </Label>
                    <Input
                      id="brand"
                      value={formData.brand}
                      onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                      required
                      data-testid="brand-input"
                      placeholder="Mercedes-Benz"
                      className="h-11 text-sm border-2 border-gray-200 focus:border-purple-500 transition-colors"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="model" className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                      <Car className="w-4 h-4 text-orange-500" />
                      Модель *
                    </Label>
                    <Input
                      id="model"
                      value={formData.model}
                      onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                      required
                      data-testid="model-input"
                      placeholder="Sprinter 519"
                      className="h-11 text-sm border-2 border-gray-200 focus:border-orange-500 transition-colors"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="seats" className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                      <User className="w-4 h-4 text-teal-500" />
                      Количество мест
                    </Label>
                    <Input
                      id="seats"
                      type="number"
                      min="1"
                      max="100"
                      value={formData.seats}
                      onChange={(e) => setFormData({ ...formData, seats: e.target.value })}
                      data-testid="seats-input"
                      placeholder="23"
                      className="h-11 text-sm border-2 border-gray-200 focus:border-teal-500 transition-colors"
                    />
                    <p className="text-xs text-gray-500">Для расчёта почасовой ставки водителя</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="mileage" className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                      <Gauge className="w-4 h-4 text-red-500" />
                      Текущий пробег (км) *
                    </Label>
                    <Input
                      id="mileage"
                      type="number"
                      value={formData.mileage}
                      onChange={(e) => setFormData({ ...formData, mileage: e.target.value })}
                      required
                      data-testid="mileage-input"
                      placeholder="125000"
                      className="h-11 text-sm border-2 border-gray-200 focus:border-red-500 transition-colors"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="mileage_note" className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                      <FileText className="w-4 h-4 text-amber-600" />
                      Заметки
                    </Label>
                    <textarea
                      id="mileage_note"
                      value={formData.mileage_note}
                      onChange={(e) => setFormData({ ...formData, mileage_note: e.target.value })}
                      placeholder="Например: последняя проверка 15.11.2024, замечания, планы и т.д."
                      rows={3}
                      className="w-full px-3 py-2 text-sm border-2 border-gray-200 focus:border-amber-500 rounded-md transition-colors resize-none focus:outline-none focus:ring-0"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="photo" className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                      <Image className="w-4 h-4 text-cyan-500" />
                      Фото автобуса
                    </Label>
                    <Input
                      id="photo"
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoChange}
                      data-testid="photo-input"
                      className="h-11 text-sm border-2 border-gray-200 focus:border-cyan-500 transition-colors file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-cyan-50 file:text-cyan-700 hover:file:bg-cyan-100"
                    />
                  </div>

                  <div className="md:col-span-2 space-y-2">
                    <Label htmlFor="notes" className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-indigo-500" />
                      Другая информация
                    </Label>
                    <textarea
                      id="notes"
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      data-testid="notes-input"
                      placeholder="Любые заметки или дополнительная информация об автобусе..."
                      rows="4"
                      className="w-full px-3 py-2 text-sm border-2 border-gray-200 focus:border-indigo-500 rounded-lg transition-colors resize-none focus:outline-none focus:ring-2 focus:ring-indigo-200"
                    />
                  </div>

                  {/* Fuel Consumption Thresholds */}
                  <div className="md:col-span-2 space-y-3 pt-4 mt-4 border-t-2 border-orange-100">
                    <div className="flex items-center gap-2 mb-3">
                      <Gauge className="w-5 h-5 text-orange-500" />
                      <h4 className="text-sm font-bold text-gray-800">Пороги расхода топлива (л/100км)</h4>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label htmlFor="red_low" className="text-sm font-medium text-gray-700 flex items-center gap-2">
                          <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                          Красный НИЗКИЙ
                        </Label>
                        <Input
                          id="red_low"
                          type="number"
                          step="0.1"
                          min="0"
                          value={formData.red_threshold_low}
                          onChange={(e) => setFormData({ ...formData, red_threshold_low: parseFloat(e.target.value) || 20 })}
                          placeholder="20"
                          className="border-2 border-red-200 focus:border-red-500"
                        />
                        <p className="text-xs text-red-600">Расход &lt; этого значения = 🔴 + Telegram</p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="yellow_low" className="text-sm font-medium text-gray-700 flex items-center gap-2">
                          <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                          Жёлтый НИЗКИЙ
                        </Label>
                        <Input
                          id="yellow_low"
                          type="number"
                          step="0.1"
                          min="0"
                          value={formData.yellow_threshold_low}
                          onChange={(e) => setFormData({ ...formData, yellow_threshold_low: parseFloat(e.target.value) || 25 })}
                          placeholder="25"
                          className="border-2 border-yellow-200 focus:border-yellow-500"
                        />
                        <p className="text-xs text-gray-500">Начало жёлтой зоны</p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="yellow_high" className="text-sm font-medium text-gray-700 flex items-center gap-2">
                          <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                          Жёлтый ВЫСОКИЙ
                        </Label>
                        <Input
                          id="yellow_high"
                          type="number"
                          step="0.1"
                          min="0"
                          value={formData.yellow_threshold_high}
                          onChange={(e) => setFormData({ ...formData, yellow_threshold_high: parseFloat(e.target.value) || 30 })}
                          placeholder="30"
                          className="border-2 border-yellow-200 focus:border-yellow-500"
                        />
                        <p className="text-xs text-gray-500">Конец зелёной зоны</p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="red_high" className="text-sm font-medium text-gray-700 flex items-center gap-2">
                          <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                          Красный ВЫСОКИЙ
                        </Label>
                        <Input
                          id="red_high"
                          type="number"
                          step="0.1"
                          min="0"
                          value={formData.red_threshold_high}
                          onChange={(e) => setFormData({ ...formData, red_threshold_high: parseFloat(e.target.value) || 35 })}
                          placeholder="35"
                          className="border-2 border-red-200 focus:border-red-500"
                        />
                        <p className="text-xs text-red-600">Расход &gt; этого значения = 🔴 + Telegram</p>
                      </div>
                    </div>
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-l-4 border-blue-400 p-3 rounded">
                      <p className="text-xs text-blue-800 space-y-1">
                        <strong>📊 Цветовая схема расхода:</strong><br/>
                        <span className="inline-block">🔴 <strong>0 - {formData.red_threshold_low}</strong> л/100км → Красный + Telegram</span><br/>
                        <span className="inline-block">🟡 <strong>{formData.red_threshold_low} - {formData.yellow_threshold_low}</strong> л/100км → Жёлтый</span><br/>
                        <span className="inline-block">🟢 <strong>{formData.yellow_threshold_low} - {formData.yellow_threshold_high}</strong> л/100км → Зелёный (норма)</span><br/>
                        <span className="inline-block">🟡 <strong>{formData.yellow_threshold_high} - {formData.red_threshold_high}</strong> л/100км → Жёлтый</span><br/>
                        <span className="inline-block">🔴 <strong>{formData.red_threshold_high}+</strong> л/100км → Красный + Telegram</span>
                      </p>
                    </div>
                  </div>
                </div>
                
                {photoPreview && (
                  <div className="mt-4 pt-4 border-t-2 border-blue-100">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-medium text-gray-600">Предпросмотр:</p>
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        onClick={async () => {
                          if (window.confirm('Удалить фото автобуса?')) {
                            try {
                              if (bus?.id) {
                                await axios.delete(`${API}/buses/${bus.id}/photo`, getAuthHeaders());
                                toast.success('Фото удалено');
                              }
                              setPhotoPreview(null);
                              setPhotoFile(null);
                            } catch (error) {
                              toast.error('Ошибка удаления фото');
                            }
                          }
                        }}
                        className="h-8 px-3"
                      >
                        <X className="w-4 h-4 mr-1" />
                        Удалить фото
                      </Button>
                    </div>
                    <div className="w-full rounded-xl overflow-hidden border-2 border-gray-200 bg-gray-100" style={{aspectRatio: '16/9', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                      <img
                        src={photoPreview}
                        alt="Preview"
                        className="w-full h-full object-contain"
                      />
                    </div>
                  </div>
                )}
                </div>
              )}

              {/* Tab: Параметры обслуживания */}
              {activeTab === 'service' && (
                <div className="bg-white rounded-xl shadow-sm border-2 border-green-100 p-4 sm:p-6">
              <div className="flex items-center gap-2 mb-4 pb-3 border-b-2 border-green-100">
                <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center">
                  <Wrench className="w-5 h-5 text-white" strokeWidth={2.5} />
                </div>
                <h3 className="text-lg font-bold text-gray-800 flex-1">Параметры обслуживания</h3>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setTypesDialogOpen(true)}
                  data-testid="manage-types-button"
                  className="flex-shrink-0 border-2 border-green-200 hover:bg-green-50"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Управление типами
                </Button>
              </div>
              
                  <div className="space-y-3 sm:space-y-4">
                {parameters.length === 0 && (
                  <div className="text-center py-8 sm:py-12 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border-2 border-dashed border-gray-300">
                    <Wrench className="w-12 h-12 mx-auto text-gray-400 mb-3" />
                    <p className="text-gray-500 text-sm font-medium mb-2">Параметры не добавлены</p>
                    <p className="text-gray-400 text-xs">Нажмите кнопку ➕ в правом нижнем углу</p>
                  </div>
                )}

                {parameters.map((param, index) => (
                  <div
                    key={index}
                    className="p-4 sm:p-5 border-2 border-green-200 rounded-lg sm:rounded-xl bg-gradient-to-br from-white to-green-50/30 shadow-md hover:shadow-xl transition-all space-y-3 sm:space-y-4"
                  >
                    <div className="flex justify-between items-center bg-gradient-to-r from-green-100 to-emerald-100 -mx-4 sm:-mx-5 -mt-4 sm:-mt-5 px-4 sm:px-5 py-2.5 sm:py-3 rounded-t-lg sm:rounded-t-xl border-b-2 border-green-300">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 bg-gradient-to-br from-green-600 to-green-700 rounded-md flex items-center justify-center">
                          <span className="text-white text-xs font-bold">{index + 1}</span>
                        </div>
                        <h4 className="text-sm sm:text-base font-bold text-gray-800">
                          {param.parameter_name || 'Новый параметр'}
                        </h4>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeParameter(index)}
                        className="text-red-600 hover:text-red-800 hover:bg-red-100 h-8 w-8 sm:h-9 sm:w-9 p-0 rounded-lg transition-all"
                        data-testid={`remove-param-${index}`}
                        title="Удалить параметр"
                      >
                        <X className="w-4 h-4 sm:w-5 sm:h-5" strokeWidth={2.5} />
                      </Button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                      <div className="space-y-1.5 sm:space-y-2">
                        <Label className="text-xs sm:text-sm font-semibold text-gray-700 flex items-center gap-1.5">
                          <Settings className="w-3.5 h-3.5 text-green-600" />
                          Тип параметра *
                        </Label>
                        <select
                          value={param.parameter_type_id || ''}
                          onChange={(e) => {
                            const value = String(e.target.value);
                            console.log('Selected parameter type:', value);
                            console.log('Available types:', parameterTypes.map(pt => ({id: pt.id, type: typeof pt.id})));
                            
                            const selectedType = parameterTypes.find(pt => String(pt.id) === value);
                            console.log('Found type:', selectedType);
                            
                            if (!selectedType) {
                              console.error('Could not find parameter type with id:', value);
                              toast.error('Ошибка выбора типа параметра');
                              return;
                            }
                            
                            // Обновляем все поля сразу одним вызовом
                            const updated = [...parameters];
                            updated[index] = {
                              ...updated[index],
                              parameter_type_id: value,
                              parameter_name: selectedType.name,
                              tracking_type: selectedType.tracking_type || 'date'
                            };
                            
                            // Если параметр по пробегу, устанавливаем текущий пробег как start_mileage
                            if (selectedType.tracking_type === 'mileage') {
                              const currentMileage = parseInt(formData.mileage) || 0;
                              updated[index].start_mileage = currentMileage;
                              updated[index].end_mileage = null; // будет заполнено кнопками
                              updated[index].start_date = '';
                              updated[index].end_date = '';
                            } else {
                              // Если параметр по дате, устанавливаем текущую дату БЕЗ timezone
                              const now = new Date();
                              const year = now.getFullYear();
                              const month = String(now.getMonth() + 1).padStart(2, '0');
                              const day = String(now.getDate()).padStart(2, '0');
                              updated[index].start_date = `${year}-${month}-${day}`;
                              updated[index].end_date = '';
                              updated[index].start_mileage = null;
                              updated[index].end_mileage = null;
                            }
                            
                            console.log('Updated parameter:', updated[index]);
                            setParameters(updated);
                          }}
                          className="flex h-10 w-full items-center justify-between rounded-lg border-2 border-green-200 focus:border-green-500 bg-white px-3 py-2 text-xs sm:text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors"
                          data-testid={`parameter-type-${index}`}
                          required
                        >
                          <option value="">Выберите тип</option>
                          {parameterTypes && parameterTypes.length > 0 ? (
                            parameterTypes.map((pt) => (
                              <option key={pt.id} value={String(pt.id)}>
                                {pt.name}
                              </option>
                            ))
                          ) : (
                            <option disabled>Загрузка...</option>
                          )}
                        </select>
                      </div>

                      <div className="space-y-1 sm:space-y-2">
                        <Label className="text-xs sm:text-sm font-semibold text-gray-700 flex items-center gap-1.5">
                          {param.tracking_type === 'mileage' ? (
                            <Gauge className="w-3.5 h-3.5 text-blue-600" />
                          ) : (
                            <Calendar className="w-3.5 h-3.5 text-purple-600" />
                          )}
                          Отслеживать по *
                        </Label>
                        <Select
                          value={param.tracking_type || ''}
                          onValueChange={(value) => updateParameter(index, 'tracking_type', value)}
                        >
                          <SelectTrigger className="h-10 border-2 border-green-200 focus:border-green-500 text-xs sm:text-sm rounded-lg">
                            <SelectValue placeholder="Способ отслеживания" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="date">📅 По дате</SelectItem>
                            <SelectItem value="mileage">🚗 По пробегу</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-[10px] sm:text-xs text-gray-500 leading-tight">Можно изменить вручную</p>
                      </div>

                      {param.tracking_type === 'mileage' ? (
                        <>
                          <div className="space-y-1.5 sm:space-y-2">
                            <Label className="text-xs sm:text-sm font-semibold text-gray-700 flex items-center gap-1.5">
                              <Gauge className="w-3.5 h-3.5 text-blue-600" />
                              Начало (км)
                            </Label>
                            <Input
                              type="number"
                              value={param.start_mileage || ''}
                              onChange={(e) => updateParameter(index, 'start_mileage', e.target.value)}
                              placeholder="0"
                              data-testid={`param-start-mileage-${index}`}
                              className="text-xs sm:text-sm h-10 border-2 border-blue-200 focus:border-blue-500"
                            />
                          </div>
                          <div className="space-y-1.5 sm:space-y-2">
                            <Label className="text-xs sm:text-sm font-semibold text-gray-700 flex items-center gap-1.5">
                              <Gauge className="w-3.5 h-3.5 text-red-600" />
                              Окончание (км)
                            </Label>
                            <Input
                              type="number"
                              value={param.end_mileage || ''}
                              onChange={(e) => updateParameter(index, 'end_mileage', e.target.value)}
                              placeholder="Выберите кнопками"
                              data-testid={`param-end-mileage-${index}`}
                              className="text-xs sm:text-sm h-10 border-2 border-red-200 focus:border-red-500"
                            />
                          </div>

                          <div className="col-span-1 md:col-span-2 bg-blue-50/50 rounded-lg p-3 border border-blue-200">
                            <Label className="mb-2 block text-xs sm:text-sm font-bold text-gray-700 flex items-center gap-1.5">
                              <Gauge className="w-4 h-4 text-blue-600" />
                              Быстрый выбор интервала:
                            </Label>
                            <div className="flex flex-wrap gap-2">
                              {getMileageQuickButtons(param.parameter_name).map((btn, btnIndex) => (
                                <Button
                                  key={btnIndex}
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  onClick={() => setQuickMileagePeriod(index, btn.value)}
                                  className="bg-white hover:bg-blue-100 border-2 border-blue-300 hover:border-blue-500 text-xs sm:text-sm h-9 sm:h-10 px-3 sm:px-4 flex-shrink-0 font-semibold shadow-sm hover:shadow transition-all"
                                >
                                  <Gauge className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5" />
                                  {btn.label}
                                </Button>
                              ))}
                            </div>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="space-y-1.5 sm:space-y-2">
                            <Label className="text-xs sm:text-sm font-semibold text-gray-700 flex items-center gap-1.5">
                              <Calendar className="w-3.5 h-3.5 text-green-600" />
                              Начало * (гггг/мм/дд)
                            </Label>
                            <DateInput
                              value={param.start_date || ''}
                              onChange={(e) => updateParameter(index, 'start_date', e.target.value)}
                              data-testid={`param-start-${index}`}
                              className="text-xs sm:text-sm h-10 border-2 border-green-200 focus:border-green-500"
                              placeholder="гггг/мм/дд"
                            />
                          </div>
                          <div className="space-y-1.5 sm:space-y-2">
                            <Label className="text-xs sm:text-sm font-semibold text-gray-700 flex items-center gap-1.5">
                              <Calendar className="w-3.5 h-3.5 text-purple-600" />
                              Окончание * (гггг/мм/дд)
                            </Label>
                            <DateInput
                              value={param.end_date || ''}
                              onChange={(e) => updateParameter(index, 'end_date', e.target.value)}
                              data-testid={`param-end-${index}`}
                              className="text-xs sm:text-sm h-10 border-2 border-purple-200 focus:border-purple-500"
                              placeholder="гггг/мм/дд"
                            />
                          </div>

                          <div className="col-span-1 md:col-span-2 bg-purple-50/50 rounded-lg p-3 border border-purple-200">
                            <Label className="mb-2 block text-xs sm:text-sm font-bold text-gray-700 flex items-center gap-1.5">
                              <Calendar className="w-4 h-4 text-purple-600" />
                              Быстрый выбор периода:
                            </Label>
                            <div className="flex flex-wrap gap-2">
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                onClick={() => setQuickDatePeriod(index, 6)}
                                className="bg-white hover:bg-green-100 border-2 border-green-300 hover:border-green-500 text-xs sm:text-sm h-9 sm:h-10 px-3 sm:px-4 flex-shrink-0 font-semibold shadow-sm hover:shadow transition-all"
                              >
                                <Calendar className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5" />
                                6 мес
                              </Button>
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                onClick={() => setQuickDatePeriod(index, 12)}
                                className="bg-white hover:bg-blue-100 border-2 border-blue-300 hover:border-blue-500 text-xs sm:text-sm h-9 sm:h-10 px-3 sm:px-4 flex-shrink-0 font-semibold shadow-sm hover:shadow transition-all"
                              >
                                <Calendar className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5" />
                                1 год
                              </Button>
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                onClick={() => setQuickDatePeriod(index, 24)}
                                className="bg-white hover:bg-purple-100 border-2 border-purple-300 hover:border-purple-500 text-xs sm:text-sm h-9 sm:h-10 px-3 sm:px-4 flex-shrink-0 font-semibold shadow-sm hover:shadow transition-all"
                              >
                                <Calendar className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5" />
                                2 года
                              </Button>
                            </div>
                          </div>
                        </>
                      )}

                      <div className="col-span-1 md:col-span-2 space-y-1.5 sm:space-y-2">
                        <Label className="text-xs sm:text-sm font-semibold text-gray-700 flex items-center gap-1.5">
                          <Settings className="w-3.5 h-3.5 text-gray-500" />
                          Описание
                        </Label>
                        <Input
                          value={param.description || ''}
                          onChange={(e) => updateParameter(index, 'description', e.target.value)}
                          placeholder="Дополнительная информация о параметре"
                          data-testid={`param-desc-${index}`}
                          className="text-sm h-10 border-2 border-gray-200 focus:border-gray-400"
                        />
                      </div>
                    </div>
                  </div>
                ))}
                  </div>
                </div>
              )}
            </div>
            
            {/* FAB кнопка Добавить параметр - показывается только на вкладке параметров */}
            {activeTab === 'service' && (
              <Button
              type="button"
              onClick={addParameter}
              data-testid="add-parameter-button"
              className="fixed bottom-8 right-8 w-16 h-16 rounded-full bg-gradient-to-br from-green-500 via-green-600 to-emerald-600 hover:from-green-600 hover:via-green-700 hover:to-emerald-700 shadow-2xl hover:shadow-3xl transform hover:scale-110 active:scale-95 transition-all duration-200 z-50 flex items-center justify-center p-0 border-4 border-white"
              title="Добавить параметр"
            >
              <Plus className="w-8 h-8 text-white" strokeWidth={3} />
            </Button>
            )}
          </form>
        </DialogContent>
      </Dialog>

      <ParameterTypesDialog
        open={typesDialogOpen}
        onOpenChange={setTypesDialogOpen}
        onTypesUpdated={fetchParameterTypes}
      />

      <ImageCropDialog
        open={cropDialogOpen}
        onClose={() => setCropDialogOpen(false)}
        imageSrc={tempImageSrc}
        onCropComplete={handleCropComplete}
      />
    </>
  );
}
